import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { useQueryParamsEffect, useQueryParamTerm } from 'pricing-pages-ui/utils/urls/queryParamUtils';
import { PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import { NavItemEnum } from '../../../constants/routing/NavItems';
import Screens from '../../../constants/routing/Screens';
import { useAnchorLinkScroll } from '../../../hooks/htmlElementHooks';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { useTrackRetail } from '../../../hooks/useTrackRetail';
import { usePageParams } from '../../../hooks/usePageParams';
import { hasPricingAndPurchasingFailedSelector, hasPricingAndPurchasingSucceededSelector } from '../../../selectors/LoadingStateSelectors';
import ProductInfoContainer from '../../common/ProductInfoContainer';
import FailedLoadBanner from '../../common/loading/FailedLoadBanner';
import FallbackErrorBanner from '../../common/loading/FallbackErrorBanner';
import { SuiteCalculator } from '../../calculator/SuiteCalculator';
import CompareStatusFooter from '../../compare/CompareStatusFooter';
import { useFeatureStore } from '../../../hooks/useFeatureStore';
import { getHighlightsForProductLine, getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import * as ProductLines from 'self-service-api/constants/ProductLines';
import { getMerchCardsToShow } from '../../../constants/merchCards/MerchCardsToShow';
import IntegrationsSection from '../../common/Integrations';
import CodeSplitEnvWrapper from '../../common/env/CodeSplitEnvWrapper';
import Faq from '../../faqs/Faq';
import { FaqsConfig } from '../../../constants/faqs/FaqsConfig';
import Question from '../../faqs/Question';
import FooterMicrocopy from '../../faqs/FooterMicrocopy';
import ProductPageTitle, { ProductPageHeader } from '../../product-pages/ProductPageTitle';
import ProductPageSubtitle from '../../product-pages/ProductPageSubtitle';
import MobileDropdownNav from '../MobileDropdownNav';
import MobileSuiteTileContainer from './MobileSuiteTileContainer';
import MobileWhyGoHubSpotSection from '../MobileWhyGoHubSpotSection';
import SegmentedTabs from '../../layout/tabs/SegmentedTabs';
import { isAppAuthSelector } from '../../../selectors/AuthSelectors';
import MobileCurrencyPicker from '../MobileCurrencyPicker';
import CustomerSupport from '../../common/CustomerSupport';
import PhoneSupportFooter from '../../common/PhoneSupportFooter';
import PhoneSupport from '../../common/PhoneSupport';
import { STARTER as STARTER_TAB } from 'pricing-pages-ui/constants/routing/Tabs';

const MobileSuitePage = () => {
  useAnchorLinkScroll();
  useTrackRetail();
  useDocumentTitle('crmSuite');
  const isAppAuth = useSelector(isAppAuthSelector);
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const hasPricingAndPurchasingFailed = useSelector(hasPricingAndPurchasingFailedSelector);
  const {
    tab
  } = usePageParams();
  const [selectedTab, setSelectedTab] = useState(tab);
  const initialProductLevel = selectedTab === STARTER_TAB ? STARTER : PROFESSIONAL;
  useEffect(() => {
    getUsageTracker().track('pageView', {
      screen: Screens.SUITE,
      tab: selectedTab
    });
  }, [selectedTab]);
  const productLevels = getMerchCardsToShow({
    tab: selectedTab
  });
  const marketingHighlightedProducts = getHighlightsForProductLine(ProductLines.MARKETING);
  const filteredMarketingHighlightedProducts = marketingHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const salesHighlightedProducts = getHighlightsForProductLine(ProductLines.SALES);
  const filteredSalesHighlightedProducts = salesHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const serviceHighlightedProducts = getHighlightsForProductLine(ProductLines.SERVICE);
  const filteredServiceHighlightedProducts = serviceHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const operationsHighlightedProducts = getHighlightsForProductLine(ProductLines.OPERATIONS);
  const filteredOperationsHighlightedProducts = operationsHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const cmsHighlightedProducts = getHighlightsForProductLine(ProductLines.CMS);
  const filteredCmsHighlightedProducts = cmsHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  useFeatureStore({
    highlightedProducts: filteredMarketingHighlightedProducts,
    productLine: ProductLines.MARKETING
  });
  useFeatureStore({
    highlightedProducts: filteredSalesHighlightedProducts,
    productLine: ProductLines.SALES
  });
  useFeatureStore({
    highlightedProducts: filteredServiceHighlightedProducts,
    productLine: ProductLines.SERVICE
  });
  useFeatureStore({
    highlightedProducts: filteredOperationsHighlightedProducts,
    productLine: ProductLines.OPERATIONS
  });
  useFeatureStore({
    highlightedProducts: filteredCmsHighlightedProducts,
    productLine: ProductLines.CMS
  });
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  return /*#__PURE__*/_jsxs("div", {
    children: [hasPricingAndPurchasingSucceeded && /*#__PURE__*/_jsx(NavMarker, {
      name: "SUITE_PAGE"
    }), !isAppAuth && /*#__PURE__*/_jsx(SegmentedTabs, {
      selectedTab: selectedTab,
      setTab: setSelectedTab,
      path: "/suite",
      bordered: true,
      use: "enclosed"
    }), /*#__PURE__*/_jsx(MobileCurrencyPicker, {}), /*#__PURE__*/_jsx(MobileDropdownNav, {
      selectedNavItem: NavItemEnum.SUITE
    }), /*#__PURE__*/_jsxs(ProductInfoContainer, {
      children: [/*#__PURE__*/_jsx(FailedLoadBanner, {
        hasFailed: hasPricingAndPurchasingFailed
      }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(ProductPageHeader, {
        children: [/*#__PURE__*/_jsx(ProductPageTitle, {
          nonProductIdentifier: NavItemEnum.SUITE
        }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
          nonProductIdentifier: NavItemEnum.SUITE
        })]
      }), /*#__PURE__*/_jsx(MobileSuiteTileContainer, {
        selectedTab: selectedTab
      }), /*#__PURE__*/_jsx(PhoneSupport, {
        phoneSourceId: "crmsuite-middle",
        screen: Screens.SUITE
      }), /*#__PURE__*/_jsx(SuiteCalculator, {
        initialProductLevel: initialProductLevel
      }), /*#__PURE__*/_jsx(MobileWhyGoHubSpotSection, {
        selectedNavItem: NavItemEnum.SUITE
      }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
        publicComponentLoader: () => import(
        /* webpackChunkName: "public-chunk" */
        `pricing-pages-ui/components/company-proof/CompanyProof`),
        publicComponentProps: {
          tab: selectedTab
        }
      }), /*#__PURE__*/_jsx(CustomerSupport, {}), /*#__PURE__*/_jsxs(Faq, {
        children: [FaqsConfig[NavItemEnum.SUITE].map((_ref) => {
          let {
            questionKey
          } = _ref,
              passthroughOptions = _objectWithoutPropertiesLoose(_ref, ["questionKey"]);

          return /*#__PURE__*/_jsx(Question, Object.assign({
            questionKey: questionKey
          }, passthroughOptions), questionKey);
        }), /*#__PURE__*/_jsx(FooterMicrocopy, {})]
      }), /*#__PURE__*/_jsx(PhoneSupportFooter, {
        phoneSourceId: "crmsuite-footer",
        screen: Screens.SUITE
      })]
    }), /*#__PURE__*/_jsx(CompareStatusFooter, {})]
  });
};

export default MobileSuitePage;